import { PlusIcon } from '@heroicons/react/24/outline';
import Input from 'antd/es/input';
import Modal from 'antd/es/modal';
import notification from 'antd/es/notification';
import { default as Select } from 'antd/es/select';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CountryFlag } from 'shared/src/CountryFlag';
import { getCountryCode } from 'shared/src/getCountryCode';

import { withSandbox } from 'utils/withSandbox';

import type { FundingConfiguration } from 'api';

import { usePutSandboxFundingsTestCase } from 'api/sandboxApi';

import { useOrganisationId } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';

import { AmountInput } from '@molecules/AmountInput/AmountInput';
import { LabelledInput } from '@molecules/LabelledInput';

import {
  SANDBOX_ADD_FUNDS_AMOUNT_INPUT_TEST_ID,
  SANDBOX_ADD_FUNDS_CURRENCY_SELECT_TEST_ID,
  SANDBOX_ADD_FUNDS_MODAL_TEST_ID,
  SANDBOX_ADD_FUNDS_SUBMIT_TEST_ID
} from './constants';
import { SandboxAlert } from './SandboxAlert';
import { SandboxBadge } from './SandboxBadge';
import { sandboxT } from './translations';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  fundingInstructions: FundingConfiguration[];
};

const { Option } = Select;

const getInstructionAccountValue = (
  instruction?: FundingConfiguration
): string => {
  const b = instruction?.beneficiary;
  return b?.iban || b?.account_number || b?.wallet_address || '';
};

const DEFAULT_TEST_CASE = 'complete_funding';

const SandboxAddFundsModalInner = ({
  open,
  setOpen,
  fundingInstructions
}: Props) => {
  const organisationId = useOrganisationId<number>();

  const instructionMap = useMemo(
    () =>
      Object.fromEntries(
        fundingInstructions.filter((i) => i.uuid).map((i) => [i.uuid, i])
      ),
    [fundingInstructions]
  );

  const firstUuid = fundingInstructions?.[0]?.uuid;

  const [currentInstructionUuid, setCurrentInstructionUuid] = useState<
    string | undefined
  >(firstUuid);

  const [amount, setAmount] = useState<string>('');

  useEffect(() => {
    if (!currentInstructionUuid || !instructionMap[currentInstructionUuid]) {
      setCurrentInstructionUuid(firstUuid);
    }
  }, [firstUuid, instructionMap, currentInstructionUuid]);

  useEffect(() => {
    if (!open) {
      setAmount('');
      setCurrentInstructionUuid(firstUuid);
    }
  }, [open, firstUuid]);

  const selectedInstruction = currentInstructionUuid
    ? instructionMap[currentInstructionUuid]
    : undefined;

  const { fundingReference, debtorAccountNumber, selectedCurrency } = useMemo(
    () => ({
      fundingReference: selectedInstruction?.funding_reference ?? '',
      debtorAccountNumber: getInstructionAccountValue(selectedInstruction),
      selectedCurrency: selectedInstruction?.currency_iso
    }),
    [selectedInstruction]
  );

  const { mutateAsync, isPending } = usePutSandboxFundingsTestCase();

  const canSubmit =
    Boolean(organisationId) &&
    Boolean(currentInstructionUuid) &&
    Boolean(fundingReference) &&
    Boolean(debtorAccountNumber) &&
    Boolean(amount);

  const onSubmit = useCallback(async () => {
    if (!organisationId || !currentInstructionUuid) return;

    try {
      await mutateAsync({
        organisationId,
        data: {
          test_case: DEFAULT_TEST_CASE,
          funding_configuration_uuid: currentInstructionUuid,
          funding_reference: fundingReference,
          amount,
          debtor_account_number: debtorAccountNumber
        }
      });

      notification.success({
        message: sandboxT.addFunds.notifications.successTitle,
        description: sandboxT.addFunds.notifications.successBody
      });

      setOpen(false);
    } catch (_) {
      notification.error({
        message: sandboxT.addFunds.notifications.errorTitle,
        description: sandboxT.addFunds.notifications.errorBody
      });
    }
  }, [
    amount,
    currentInstructionUuid,
    debtorAccountNumber,
    fundingReference,
    mutateAsync,
    organisationId,
    setOpen
  ]);

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      footer={null}
      title={
        <div className="flex items-center gap-2 text-xl">
          <PlusIcon
            className="size-6 stroke-current text-black"
            strokeWidth="1.0"
          />

          <div>{sandboxT.addFunds.title}</div>
          <SandboxBadge />
        </div>
      }
    >
      <div
        className="flex flex-col"
        data-testid={SANDBOX_ADD_FUNDS_MODAL_TEST_ID}
      >
        <div className="py-4">
          <SandboxAlert
            message={
              <>
                {sandboxT.addFunds.alert} {sandboxT.common.documentation.see}{' '}
                <a href="/docs/guide/sandbox">
                  {sandboxT.common.documentation.label}
                </a>{' '}
                {sandboxT.common.documentation.forMoreDetails}
              </>
            }
          />
        </div>

        <div className="flex flex-col gap-4">
          <LabelledInput label={sandboxT.common.labels.currencyOption}>
            <Select
              value={currentInstructionUuid}
              className="w-full"
              onChange={(value) => setCurrentInstructionUuid(value)}
              data-testid={SANDBOX_ADD_FUNDS_CURRENCY_SELECT_TEST_ID}
            >
              {fundingInstructions.map((instruction) => (
                <Option key={instruction.uuid} value={instruction.uuid}>
                  <div className="flex flex-row items-center gap-2 text-sm">
                    <CountryFlag
                      className="!size-[22px]"
                      countryCode={getCountryCode(instruction.currency_iso)}
                      svg
                    />
                    <span>{instruction.currency_iso}</span>
                  </div>
                </Option>
              ))}
            </Select>
          </LabelledInput>

          <div className="grid grid-cols-1 gap-3">
            <LabelledInput
              label={sandboxT.common.labels.fundingReference}
              tooltipText={sandboxT.common.tooltips.fundingInstructions}
            >
              <Input value={fundingReference} disabled />
            </LabelledInput>

            <LabelledInput
              label={sandboxT.common.labels.beneficiaryAccount}
              tooltipText={sandboxT.common.tooltips.fundingInstructions}
            >
              <Input value={debtorAccountNumber} disabled />
            </LabelledInput>

            <LabelledInput label={sandboxT.common.labels.amount}>
              <AmountInput
                currency={selectedCurrency}
                value={amount}
                onChange={setAmount}
                placeholder={sandboxT.common.placeholders.amount}
                data-testid={SANDBOX_ADD_FUNDS_AMOUNT_INPUT_TEST_ID}
              />
            </LabelledInput>
          </div>

          <PrimaryButton
            icon={<PlusIcon className="size-5 stroke-current" />}
            disabled={!canSubmit}
            loading={isPending}
            onClick={onSubmit}
            data-testid={SANDBOX_ADD_FUNDS_SUBMIT_TEST_ID}
          >
            {sandboxT.common.actions.addFunds}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

export const SandboxAddFundsModal = withSandbox(SandboxAddFundsModalInner);
